import { Button, Checkbox, Container } from '@cluey/cluey-components';
import { orderBy } from 'lodash';
import { useContext, useState } from 'react';
import { useDispatch } from 'react-redux';

import umbrellaIcon from '../../../../assets/images/icon-filled-umbrella-beach.svg';
import { BackButton } from '../../../common/BackButton';
import { useMultiStep } from '../../../common/multi-step';
import { submitCaseToSF } from '../../../legacy/actions';
import api from '../../../legacy/api';
import { Loader } from '../../../legacy/components';
import { Session } from '../../../legacy/types';
import { PauseEnrolmentContext } from '../PauseEnrolmentContext';
import { formatSessionSchedule, formatSessionScheduleWithTime, isEligibleForSummerDiscount } from '../helpers';
import useSchoolPeriods from '../hooks/useSchoolPeriods';

export const MAX_SESSIONS_FOR_DISCOUNT = 5;

export const addOrRemoveSessionFromList = (sessions: Session[], session: Session, operation: 'add' | 'remove') => {
  if (operation === 'add') {
    // do not add if already in the list
    if (sessions.some((s) => s.studentSessionId === session.studentSessionId)) {
      return sessions;
    }
    return [...sessions, session];
  } else if (operation === 'remove') {
    return sessions.filter((s) => s.studentSessionId !== session.studentSessionId);
  } else {
    console.warn('addOrRemoveSessionFromList - Invalid operation: ', operation);
    return sessions;
  }
};

const SummerDiscountOffer = ({
  sessionsToApplyDiscountTo,
  setSessionsToApplyDiscountTo,
}: {
  sessionsToApplyDiscountTo: Session[];
  setSessionsToApplyDiscountTo: (sessions: Session[]) => void;
}) => {
  const { goToStep } = useMultiStep();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const { selectedEnrolment, timezone } = useContext(PauseEnrolmentContext);
  const { currentOrUpcomingHoliday } = useSchoolPeriods();
  const { data: eligibleSessions } = api.session.all.useQuery('upcoming', {
    select: (data) => {
      const filteredSessions = data.filter(
        (session) =>
          session.uniqueEnrolmentId === selectedEnrolment.enrolmentId &&
          isEligibleForSummerDiscount(session, currentOrUpcomingHoliday)
      );
      return orderBy(filteredSessions, ['startTime'], ['asc']);
    },
  });
  const dispatch = useDispatch();
  const formattedSelectedSessionsList = sessionsToApplyDiscountTo.reduce((acc, session) => {
    acc.push(
      `${formatSessionScheduleWithTime(session, timezone, true)} Student session id: ${session.studentSessionId}`
    );
    return acc;
  }, []);
  const sessionsToDisplay = eligibleSessions.slice(0, MAX_SESSIONS_FOR_DISCOUNT);

  const handleOnChangeSelectedSessions = (selected: boolean, session: Session) => {
    const operation = selected ? 'add' : 'remove';
    const updatedValue = addOrRemoveSessionFromList(sessionsToApplyDiscountTo, session, operation);
    setSessionsToApplyDiscountTo(updatedValue);
  };

  const handleAcceptOffer = () => {
    if (sessionsToApplyDiscountTo.length <= 0) {
      setSubmitError('Please select at least one session to apply the discount to.');
    } else {
      const message = `
      The customer has opted to take discounted summer sessions instead of pausing. Please apply 20% off their below unpaid sessions:
      \n\n${formattedSelectedSessionsList.join('\n')}
      \n\nTimes are in the customer account's timezone, please double check the sessions before applying the discounts.
      `;
      setSubmitting(true);
      const payload = {
        caseRecordType: 'Support',
        caseRequestFrom: 'Parent',
        caseType: 'Payment',
        caseCategory: 'Support Request',
        caseSubject: 'Apply summer engagement discount',
        caseSubCategory: 'Additional discount requested',
        message,
        uniqueEnrolmentId: selectedEnrolment.enrolmentId,
        onSubmitSuccess: () => {
          goToStep('summer-discount-offer-success');
        },
        onSubmitFailed: () => {
          setSubmitting(false);
          setSubmitError(
            'There was a problem with submitting the request to apply the discount. Please try again later.'
          );
        },
      };
      dispatch(submitCaseToSF(payload));
    }
  };

  if (submitting) {
    return (
      <Container className="items-center">
        <div className="mx-auto w-fit">
          <Loader />
        </div>
      </Container>
    );
  }

  return (
    <Container className="items-center text-center">
      <div className="mb-1 flex">
        <BackButton />
      </div>
      <div className="rounded-lg border border-grey-2 p-10 shadow-lg">
        <div className="mx-auto mb-6 w-fit rounded bg-yellow-3 px-2 font-bold uppercase text-black">
          Limited time offer
        </div>
        <div className="mx-auto max-w-[620px]">
          <h1 className="proxima-nova my-5 text-3xl font-bold">Discounted summer sessions offer</h1>
          <div className="mb-8">
            Make the most of this summer and enjoy a 20% discount on summer sessions instead of pausing your enrolment.
          </div>
          {sessionsToDisplay?.length > 0 && (
            <div className="mx-auto w-fit items-start text-left">
              <div className="mb-3 flex w-fit flex-row items-center">
                <img className="mr-2" width={20} height={20} src={umbrellaIcon} alt="umbrella" />
                <div className="pt-1 text-xs font-bold uppercase">Your discounted summer sessions</div>
              </div>
              <div className="ml-2 w-fit border-l-4 border-grey-2 pl-5">
                <div className="mb-5">
                  <ul className="space-y-1 text-sm leading-snug">
                    {sessionsToDisplay.map((session) => (
                      <div key={session.studentSessionId} className="flex items-center gap-x-3">
                        <div className="shrink-0">
                          <Checkbox
                            id={session.studentSessionId}
                            onChange={(value) => handleOnChangeSelectedSessions(value, session)}
                            value={sessionsToApplyDiscountTo.some(
                              (s) => s.studentSessionId === session.studentSessionId
                            )}
                          />
                        </div>
                        <div className="flex flex-col gap-y-1">
                          <label className="cursor-pointer text-sm" htmlFor={session.studentSessionId}>
                            {formatSessionSchedule(session)}
                          </label>
                        </div>
                      </div>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="mt-10">
          {submitError && <div className="my-1 text-sm text-red-5">{submitError}</div>}
          <Button size="lg" className="border-none" onClick={handleAcceptOffer}>
            Claim summer discount offer
          </Button>
        </div>
        <div className="mt-3">
          <Button
            size="lg"
            appearance="reverse"
            className="border-none"
            onClick={() => {
              goToStep('select-pause-dates');
            }}
          >
            Continue with pause request
          </Button>
        </div>
      </div>
    </Container>
  );
};

export default SummerDiscountOffer;
